import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as _ from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class FtCommentsService {

  constructor(private http: HttpClient) { }

  /**
   * Save comments
   * Action Code: 'zyk0j5'
   * 
   * AngularJS equivalent (ftcCommentsService.js line 24-58):
   * - URL: GENERAL_CONFIG.base_url + "/saveJsonObject?action_code=zyk0j5"
   * - Method: POST with form-urlencoded body (like all other saveJsonObject services)
   * - Params: merge(globalParams, filterParams) + clob_data + clob_settings + otherInfo fields
   */
  saveComments(
    baseUrl: string,
    returnClobSettingsObj: any,
    defaultsChangeDetails: any[],
    otherInfo: any,
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    
    // Validate baseUrl
    const baseUrlStr = baseUrl ? baseUrl.toString().trim() : '';
    if (!baseUrlStr || baseUrlStr === 'undefined' || baseUrlStr === 'null' || baseUrlStr.length === 0) {
      console.error('Invalid baseUrl:', baseUrl);
      throw new Error('Base URL is not configured');
    }
    
    // Build URL exactly like AngularJS: GENERAL_CONFIG.base_url + "/saveJsonObject?action_code=zyk0j5"
    const cleanBaseUrl = baseUrlStr.replace(/\/$/, '');
    const url = `${cleanBaseUrl}/saveJsonObject?action_code=zyk0j5`;
    
    console.log('Save comments URL:', url);

    // Build JSON strings exactly like AngularJS service (line 27-28)
    const jsonObj = JSON.stringify(defaultsChangeDetails);      // clob_data
    const jsonSettings = JSON.stringify(returnClobSettingsObj); // clob_settings

    // Build params exactly like AngularJS service (line 32-45):
    // var params = filterParams;
    // params = _.merge({}, GlobalService.globalParams, params);
    // params = _.extend({clob_data:jsonObj}, params);
    // params = _.extend({clob_settings:jsonSettings}, params);
    // params = _.extend({sso_id, tax_year, scenario, jcd_key, screen_key, object_id, comments, OPERATION_TYPE}, params);
    
    // Build params exactly like other services (e.g., cfc-investment-adj-edit.service.ts)
    let params: any = _.merge({}, globalService?.globalParams || {}, filterParams);
    params = _.extend({ clob_data: jsonObj }, params);
    params = _.extend({ clob_settings: jsonSettings }, params);
    
    // Add all required parameters with proper fallback chains (matching other services)
    params.sso_id = otherInfo.sso_id || globalService?.sso_id || globalService?.globalParams?.sso_id || '';
    params.tax_year = otherInfo.tax_year || filterParams?.tax_year || globalService?.tax_year || globalService?.globalParams?.tax_year || '';
    params.scenario = otherInfo.scenario || filterParams?.scenario || globalService?.scenario || globalService?.globalParams?.scenario || '';
    params.jcd_key = otherInfo.jcd_key || filterParams?.jcd_key || globalService?.jcd_key || globalService?.globalParams?.jcd_key || '';
    params.screen_key = otherInfo.screen_key || filterParams?.screen_key || globalService?.screen_key || globalService?.globalParams?.screen_key || '';
    params.object_id = otherInfo.object_id || '';
    params.comments = otherInfo.comments || '';
    params.OPERATION_TYPE = otherInfo.OPERATION_TYPE || 'I';
    // Required configuration parameters
    params.scenarioCode = otherInfo.scenarioCode || filterParams?.scenarioCode || globalService?.scenarioCode || globalService?.globalParams?.scenarioCode || 'RAF';
    params.is_issue_key_enabled = otherInfo.is_issue_key_enabled || filterParams?.is_issue_key_enabled || globalService?.is_issue_key_enabled || globalService?.globalParams?.is_issue_key_enabled || 'N';

    // CRITICAL: Ensure object_id is always included (even if empty) to prevent NULL insert error
    if (!params.object_id) {
      params.object_id = otherInfo.object_id || returnClobSettingsObj?.object_id || '';
    }

    console.log('Save params:', params);
    console.log('Required config - scenarioCode:', params.scenarioCode, ', is_issue_key_enabled:', params.is_issue_key_enabled);

    // Convert to URLSearchParams for form-urlencoded format (like all other saveJsonObject services)
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlEncoded.set(key, params[key].toString());
      }
    });

    // CRITICAL: Double-check that object_id is always included (even if empty) to prevent NULL insert error
    if (!urlEncoded.has('object_id')) {
      urlEncoded.set('object_id', params.object_id || otherInfo.object_id || returnClobSettingsObj?.object_id || '');
    }

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      }),
      withCredentials: true
    };

    return this.http.post<any>(url, urlEncoded.toString(), httpOptions);
  }

  /**
   * Helper to convert value to number (for tax_year, scenario, jcd_key)
   */
  private toNumber(value: any): number | string {
    if (value === undefined || value === null || value === '') {
      return '';
    }
    const num = Number(value);
    return isNaN(num) ? value : num;
  }

  /**
   * Get all comments for a specific object
   * Action Code: '8n9er0'
   * 
   * AngularJS equivalent (ftcCommentsCtrl.js line 139-151):
   * var params = { action_code: '8n9er0', screen_key, object_id, tax_year, scenario, jcd_key }
   * JsonObjectFactory.getJSONObj(GENERAL_CONFIG.base_url + '/loadJsonObject', params)
   * 
   * JsonObjectFactory.getJSONObj (GlobalServices.js line 2003-2078):
   * - Merges filterParams, then globalParams, then the passed params
   * - Uses HTTP GET with params as query parameters
   */
  getAllComments(
    baseUrl: string,
    screen_key: string,
    object_id: string,
    tax_year: string | number,
    scenario: string | number,
    jcd_key: string | number,
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    // Build the specific params like AngularJS controller (line 140-147)
    // Use proper fallback chains like other services
    const specificParams: any = {
      action_code: '8n9er0',
      screen_key: screen_key || filterParams?.screen_key || globalService?.screen_key || globalService?.globalParams?.screen_key || '',
      object_id: object_id || '',
      tax_year: tax_year || filterParams?.tax_year || globalService?.tax_year || globalService?.globalParams?.tax_year || '',
      scenario: scenario || filterParams?.scenario || globalService?.scenario || globalService?.globalParams?.scenario || '',
      jcd_key: jcd_key || filterParams?.jcd_key || globalService?.jcd_key || globalService?.globalParams?.jcd_key || ''
    };

    // Merge exactly like JsonObjectFactory.getJSONObj (line 2005-2007):
    // var paramsMerge = _.merge({}, filterParams, _params);
    // var params = _.merge({}, GlobalService.globalParams, paramsMerge);
    const paramsMerge = _.merge({}, filterParams, specificParams);
    const params = _.merge({}, globalService?.globalParams || {}, paramsMerge);

    // Build URL with query parameters
    const urlParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        urlParams.set(key, params[key].toString());
      }
    });

    const cleanBaseUrl = baseUrl.replace(/\/$/, '');
    const url = `${cleanBaseUrl}/loadJsonObject?${urlParams.toString()}`;
    
    console.log('getAllComments URL:', url);
    console.log('getAllComments merged params:', params);

    // AngularJS getJSONObj uses HTTP GET (line 2067)
    return this.http.get<any>(url, { withCredentials: true });
  }

  /**
   * Check lock status for tax return
   * Action Code: 'n10cpv'
   * 
   * AngularJS equivalent (ftcCommentsCtrl.js line 107-120):
   * var lockparams = { action_code: 'n10cpv', p_scenario: vm.scenario, p_jcd_key: vm.jcd_key }
   * JsonObjectFactory.getJSONObj(GENERAL_CONFIG.base_url+'/loadJsonObject', lockparams)
   */
  checkLockStatus(
    baseUrl: string,
    p_scenario: string | number,
    p_jcd_key: string | number,
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    // Build params exactly like AngularJS controller (line 107-108)
    // Use proper fallback chains like other services (e.g., cfc-investment-adj-edit.service.ts)
    const specificParams: any = {
      action_code: 'n10cpv',
      p_scenario: p_scenario || filterParams?.scenario || globalService?.scenario || globalService?.globalParams?.scenario || '',
      p_jcd_key: p_jcd_key || filterParams?.jcd_key || globalService?.jcd_key || globalService?.globalParams?.jcd_key || '',
      tax_year: filterParams?.tax_year || globalService?.tax_year || globalService?.globalParams?.tax_year || '',
      scenario: filterParams?.scenario || globalService?.scenario || globalService?.globalParams?.scenario || '',
      jcd_key: filterParams?.jcd_key || globalService?.jcd_key || globalService?.globalParams?.jcd_key || '',
      screen_key: filterParams?.screen_key || globalService?.screen_key || globalService?.globalParams?.screen_key || ''
    };

    // Merge like JsonObjectFactory.getJSONObj
    const paramsMerge = _.merge({}, filterParams, specificParams);
    const params = _.merge({}, globalService?.globalParams || {}, paramsMerge);

    const urlParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        urlParams.set(key, params[key].toString());
      }
    });

    const cleanBaseUrl = baseUrl.replace(/\/$/, '');
    const url = `${cleanBaseUrl}/loadJsonObject?${urlParams.toString()}`;
    
    console.log('checkLockStatus URL:', url);
    console.log('checkLockStatus merged params:', params);

    // AngularJS uses HTTP GET via getJSONObj
    return this.http.get<any>(url, { withCredentials: true });
  }
}

